import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import get from 'lodash/get';
import cx from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { withRouter } from 'react-router';

import ReactResizeDetector from 'react-resize-detector';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { withDeviceInfo } from 'shared/Responsive';

const wasOnlyModalOpenOrClosed = (location, prevLocation) => (
  get(location, 'pathName') === get(prevLocation, 'pathName') &&
  get(location, 'hash') === get(prevLocation, 'hash') &&
  get(location, 'search') === get(prevLocation, 'search') &&
  Boolean(get(location, 'state.modal') || get(prevLocation, 'state.modal'))
);

class BaseModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = { closing: false };

    this.positionModal = this.positionModal.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);

    this.componentEl = React.createRef();
    this.modalContentWrapperEl = React.createRef();
    this.modalContentEl = React.createRef();
    this.modalBodyEl = React.createRef();

    this.modalId = uniqueId('modal_');
  }

  render() {
    const { className, children, headerText } = this.props;
    const withHeader = !!headerText;

    return (
      <div
        ref={this.componentEl}
        className={cx(
          'modal',
          className,
          {
            closing: this.state.closing,
            'modal--fullscreen': this.isFullScreen(),
            'modal--with-header': withHeader
          }
        )}
      >
        <span className="modal__backdrop" />

        <div
          ref={this.modalContentWrapperEl}
          className="modal__content-wrapper"
          onClick={this.onWrapperClick.bind(this)}
        >
          <div
            ref={this.modalContentEl}
            className="modal__content"
          >
            {withHeader && (
              <div className="modal__header">
                {headerText}
              </div>
            )}

            <span
              className={cx('modal__close-button', { 'modal__close-button--white': withHeader })}
              onClick={this.closeModal.bind(this)}
            />

            <ReactResizeDetector handleWidth handleHeight onResize={this.positionModal} />
            <div className="modal__body" ref={this.modalBodyEl}>
              <div className="modal__inner-body">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.closeModal) {
      this.closeModal();
    }
  }

  componentDidUpdate(prevProps) {
    const { deviceInfo, history, location } = this.props;

    if (prevProps.location !== location) {
      if (get(prevProps, 'location.state.modal') === this.modalId && history.action === 'POP') {
        this.closeModal();
      }
    }

    if (deviceInfo.isMobile !== prevProps.deviceInfo.isMobile) {
      this.disableBodyScroll();
    }
  }

  componentDidMount() {
    this.addEventListeners();
    this.disableBodyScroll();
    this.positionModal();
    // push modal state on top of existing location
    this.props.history.push({...this.props.location, state: { modal: this.modalId }});
  }

  componentWillUnmount() {
    const { history, location } = this.props;

    clearTimeout(this.closingTimeout);
    enableBodyScroll(this.scrollElement);
    this.removeEventListeners();

    // if the current location state assumes the modal open, then pop the state from the history
    if (get(location, 'state.modal') === this.modalId) {
      history.goBack();
    }
  }

  addEventListeners() {
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
  }

  removeEventListeners() {
    document.removeEventListener('touchmove', this.onTouchMove, { passive: false });
  }

  disableBodyScroll() {
    if (this.scrollElement) enableBodyScroll(this.scrollElement);
    this.scrollElement = this.isFullScreen() ? this.modalBodyEl.current : this.modalContentWrapperEl.current;
    disableBodyScroll(this.scrollElement);
  }

  closeModal() {
    this.setState({ closing: true });
    this.closingTimeout = setTimeout(() => {
      this.props.onModalClose();
    }, 300);

    enableBodyScroll(this.scrollElement);
  }

  isIosDevice() {
    // Same detection the 'body-scroll-lock' module does
    // See: https://github.com/willmcpo/body-scroll-lock/blob/master/src/bodyScrollLock.js#L28
    return typeof window !== 'undefined' &&
      window.navigator &&
      window.navigator.platform &&
      /iP(ad|hone|od)/.test(window.navigator.platform);
  }

  isFullScreen() {
    return this.props.deviceInfo.isMobile && this.props.fullScreenOnMobile;
  }

  getModalTopOffset() {
    const modalContentHeight = parseInt(this.modalContentEl.current.offsetHeight) || 0;

    const viewportHeight = window.innerHeight;

    let newPosition = (viewportHeight - modalContentHeight) / 2;
    if (newPosition < 0) { newPosition = 0; }

    return newPosition;
  }

  positionModal() {
    // These won't work during snapshot tests, and aren't really relevant for those
    if (!this.modalContentEl.current) { return; }
    this.modalContentEl.current.style.top = `${this.getModalTopOffset()}px`;
  }

  onTouchMove(e) {
    // Preventing touchmove on Android will prevent scrolling
    // Touchmove is only an issue on iOS devices
    if (this.isIosDevice()) { e.preventDefault(); }
  }

  onWrapperClick(event) {
    if (event.target === this.modalContentWrapperEl.current) { this.closeModal(); }
  }
}

BaseModal.defaultProps = {
  className: '',
  closeModal: false,
  fullScreenOnMobile: true,
  headerText: null
};

BaseModal.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  closeModal: PropTypes.bool,
  fullScreenOnMobile: PropTypes.bool,
  headerText: PropTypes.string,
  id: PropTypes.string,
  onModalClose: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  deviceInfo: PropTypes.object.isRequired
};

const Modal = withDeviceInfo(withRouter(BaseModal));

const PortalModal = (props) => {
  return ReactDOM.createPortal(<Modal {...props} />, document.querySelector('#modal'));
};

export {
  BaseModal,
  Modal,
  wasOnlyModalOpenOrClosed
};
export default PortalModal;
